/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Lozenge from '@atlaskit/lozenge';
import Popup from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { type SearchResultDealPartial } from '@atlassian/search-client';

import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';
import { type useFireContextMenuButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { type useFireContextMenuCopyButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { type useFireResultClicked } from '../../../../../../../controllers/events/use-fire-result-clicked';
import { type useFireResultCtxMenuClicked } from '../../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { type useFireSummarizeInChatButtonClicked } from '../../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../../controllers/store/bootstrap/accessors';
import { useSelectedProduct } from '../../../../../../../controllers/store/search';

import { messages } from './messages';

const contentStyles = xcss({
	padding: 'space.200',
});

const subheadingStyles = xcss({
	color: 'color.text.subtlest',
	font: 'font.body',
});

interface DealSearchResultProps {
	partial: SearchResultDealPartial;
	/** The position in the results list shown to the user. */
	position?: number;
	icon: React.ReactNode;
	product?: string;
	onResultClicked?: ReturnType<typeof useFireResultClicked>;
	onResultCtxMenuClicked?: ReturnType<typeof useFireResultCtxMenuClicked>;
	onSummarizeInChatButtonClicked?: ReturnType<typeof useFireSummarizeInChatButtonClicked>;
	onContextMenuCopyButtonClicked?: ReturnType<typeof useFireContextMenuCopyButtonClicked>;
	onContextMenuButtonClicked?: ReturnType<typeof useFireContextMenuButtonClicked>;
	isAllProductSelected?: boolean;
}

export const DealSearchResult = ({
	partial,
	position,
	icon,
	product,
	onResultClicked,
	onResultCtxMenuClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	isAllProductSelected,
}: DealSearchResultProps) => {
	const { formatMessage, formatDate } = useIntl();

	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const appMode = useApplicationMode();
	const [isOpen, setIsOpen] = useState(false);

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');

	const { url, entity, title, lastModifiedDate, allContributors } = partial;

	const lastModifiedComponent = <LastModified lastModified={lastModifiedDate} />;

	const formattedDate = entity?.dealClosedAt
		? formatDate(new Date(entity.dealClosedAt), {
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		})
		: '';

	const closeOnDateComponent = (
		<Box xcss={contentStyles}>
			<FormattedMessage {...messages.closedOn} values={{ date: formattedDate }} />
		</Box>
	);

	const body = <Stack>{entity?.lastActivity?.event}</Stack>;

	const description = (
		<Box>
			<DescriptionBody description={body} />
			<Box xcss={subheadingStyles}>
				<LastModified lastModified={entity?.lastActivity?.lastActivityAt} />
			</Box>
		</Box>
	);

	const getOwnerName = () => {
		if (allContributors?.length && allContributors[0]?.name) {
			return allContributors[0].name;
		}
	};

	const stageText = entity?.stage || '';
	const parent = entity?.parent?.displayName;
	const truncatedStageText = stageText.length > 20 ? `${stageText.slice(0, 20)}...` : stageText;

	const headerMetadataParts = [
		formatMessage(messages.eventLabel),
		['parent', parent],
		['owner', getOwnerName()],
		['last-modified', lastModifiedComponent],
	];

	return (
		<Flex gap="space.100" justifyContent="start">
			<SearchResult
				id={partial.id}
				product={product}
				productSelected={!!selectedProduct}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultCtxMenuClicked}
				onCopyLinkButtonClicked={onCopyLinkButtonClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				icon={icon}
				description={description}
				headerMetadataParts={headerMetadataParts}
				title={enrichExcerpt(title)}
				url={url}
				isThirdParty
				cloudId={cloudId}
				primaryProduct={primaryProduct}
				position={position}
				partial={partial}
				appMode={appMode}
				footer={
					<MentionedByLinkedResults
						linkedResults={partial.linkedResults || []}
						thirdPartyConfigs={thirdPartyConfigs}
						partial={partial}
						url={url}
						position={position}
						onResultClicked={onResultClicked || (() => { })}
						onResultContextMenu={onResultCtxMenuClicked}
						isAllProductSelected={isAllProductSelected}
					/>
				}
			/>
			<Popup
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				placement="right-start"
				content={() => closeOnDateComponent}
				trigger={(triggerProps) => (
					<Tooltip content={stageText} position="auto">
						<Button
							{...triggerProps}
							isSelected={isOpen}
							appearance="subtle"
							spacing="compact"
							onClick={() => setIsOpen(!isOpen)}
						>
							<Box paddingInlineStart="space.800">
								<Lozenge>{truncatedStageText}</Lozenge>
							</Box>
						</Button>
					</Tooltip>
				)}
			/>
		</Flex>
	);
};
